import { useState, useEffect, useMemo } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, Settings, Play, Download, Check, CircleDollarSign, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const inr = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
};

export default function PayrollPage() {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'management'].includes(user?.system_role);

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState(isAdmin ? 'runs' : 'mine');
  const [payrolls, setPayrolls] = useState([]);
  const [structs, setStructs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterDivision, setFilterDivision] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [structDialog, setStructDialog] = useState({ open: false, employee: null });
  const [structForm, setStructForm] = useState({ basic_salary: '', paid_weekly_offs: 4, pf_applicable: true, esi_applicable: true, pt_applicable: false, lwf_applicable: false, lwf_amount: 50 });
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [editForm, setEditForm] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { month };
      if (filterBranch && filterBranch !== 'all') params.branch_id = filterBranch;
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      const reqs = [api.get('/payroll', { params })];
      if (isAdmin) {
        reqs.push(api.get('/salary-structures'));
        reqs.push(api.get('/employees'));
        reqs.push(api.get('/branches'));
      }
      const [pRes, sRes, eRes, bRes] = await Promise.all(reqs);
      setPayrolls(pRes.data);
      // Reset selection whenever the underlying list changes — keeps bulk
      // actions targeting only what is currently visible.
      setSelectedIds(new Set());
      if (isAdmin) {
        setStructs(sRes.data);
        setEmployees(eRes.data);
        setBranches(bRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [month, filterBranch, filterDivision]);

  const structMap = useMemo(() => {
    const m = {};
    structs.forEach(s => { m[s.employee_id] = s; });
    return m;
  }, [structs]);

  const handleRunPayroll = async () => {
    if (!window.confirm(`Generate/refresh draft payroll for ${month}? Finalized and paid entries will be skipped.`)) return;
    try {
      const res = await api.post('/payroll/run', { month });
      toast.success(`Created ${res.data.created}, updated ${res.data.updated}, skipped ${res.data.skipped}.`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleFinalize = async (id) => {
    try {
      await api.put(`/payroll/${id}/finalize`);
      toast.success('Payroll finalized. Employee notified.');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.put(`/payroll/${id}/mark-paid`);
      toast.success('Marked as Paid');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleRemoveRow = async (p) => {
    if (!window.confirm(`Remove ${p.employee_name}'s ${p.month} payroll row?\n\nUse this only when the row was created in error. Paid rows cannot be removed.`)) return;
    try {
      await api.delete(`/payroll/${p.id}`);
      toast.success('Payroll row removed');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams({ month });
      if (filterBranch && filterBranch !== 'all') params.append('branch_id', filterBranch);
      if (filterDivision && filterDivision !== 'all') params.append('division', filterDivision);
      const { data } = await api.get(`/payroll/export-csv?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paysheet_${month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Pay Sheet exported');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams({ month });
      if (filterBranch && filterBranch !== 'all') params.append('branch_id', filterBranch);
      if (filterDivision && filterDivision !== 'all') params.append('division', filterDivision);
      const { data } = await api.get(`/payroll/export-excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      const branchSlug = filterBranch !== 'all'
        ? '_' + (branches.find(b => b.id === filterBranch)?.name || 'branch').replace(/\s+/g, '_')
        : '';
      link.setAttribute('download', `paysheet_${month}${branchSlug}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Pay Sheet (Excel) downloaded');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Selection helpers — only draft rows are eligible for bulk operations.
  const draftIds = payrolls.filter(p => p.status === 'draft' || p.status === 'on_hold').map(p => p.id);
  const allDraftSelected = draftIds.length > 0 && draftIds.every(id => selectedIds.has(id));
  const toggleSelectAll = () => {
    if (allDraftSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(draftIds));
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleBulkFinalize = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error('Select draft rows first'); return; }
    if (!window.confirm(`Approve & finalize ${ids.length} payroll row(s)? Once finalized, employees can be marked paid.`)) return;
    try {
      const { data } = await api.post('/payroll/bulk-finalize', { ids });
      toast.success(`Finalized ${data.finalized} · skipped ${data.skipped}`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error('Select draft rows first'); return; }
    if (!window.confirm(`Permanently delete ${ids.length} draft payroll row(s)? Paid rows are skipped automatically.`)) return;
    try {
      const { data } = await api.post('/payroll/bulk-delete', { ids });
      toast.success(`Deleted ${data.deleted} · skipped ${data.skipped}`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openStructDialog = async (emp) => {
    try {
      const res = await api.get(`/salary-structures/${emp.id}`);
      setStructForm({
        basic_salary: res.data.basic_salary || '',
        paid_weekly_offs: res.data.paid_weekly_offs ?? 4,
        pf_applicable: res.data.pf_applicable !== false,
        esi_applicable: res.data.esi_applicable !== false,
        pt_applicable: !!res.data.pt_applicable,
        lwf_applicable: !!res.data.lwf_applicable,
        lwf_amount: res.data.lwf_amount ?? 50,
      });
      setStructDialog({ open: true, employee: emp });
    } catch { toast.error('Failed to load'); }
  };

  const handleSaveStruct = async () => {
    if (!structDialog.employee) return;
    try {
      await api.post(`/salary-structures/${structDialog.employee.id}`, {
        basic_salary: parseFloat(structForm.basic_salary) || 0,
        paid_weekly_offs: parseInt(structForm.paid_weekly_offs) || 0,
        pf_applicable: !!structForm.pf_applicable,
        esi_applicable: !!structForm.esi_applicable,
        pt_applicable: !!structForm.pt_applicable,
        lwf_applicable: !!structForm.lwf_applicable,
        lwf_amount: parseFloat(structForm.lwf_amount) || 50,
      });
      toast.success('Saved');
      setStructDialog({ open: false, employee: null });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (item) => {
    setEditForm({
      basic_salary: item.basic_salary ?? 0,
      present_days: item.present_days ?? 0,
      incentive: item.incentive ?? 0,
      advance: item.advance ?? 0,
      penalty: item.penalty ?? 0,
      other_deductions: item.other_deductions ?? 0,
      brand_salary: item.brand_salary ?? 0,
      is_brand_staff: !!item.is_brand_staff,
      pf_applicable: item.pf_applicable !== false,
      esi_applicable: item.esi_applicable !== false,
      pt_applicable: !!item.pt_applicable,
      lwf_applicable: !!item.lwf_applicable,
      lwf_amount: item.lwf_amount ?? 50,
      notes: item.notes || '',
    });
    setEditDialog({ open: true, item });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.item) return;
    try {
      const payload = { ...editForm };
      ['basic_salary', 'present_days', 'incentive', 'advance', 'penalty', 'other_deductions', 'brand_salary']
        .forEach(k => { payload[k] = parseFloat(payload[k]) || 0; });
      payload.pf_applicable = !!editForm.pf_applicable;
      payload.esi_applicable = !!editForm.esi_applicable;
      payload.pt_applicable = !!editForm.pt_applicable;
      payload.lwf_applicable = !!editForm.lwf_applicable;
      payload.lwf_amount = parseFloat(editForm.lwf_amount) || 50;
      await api.put(`/payroll/${editDialog.item.id}`, payload);
      toast.success('Payroll updated');
      setEditDialog({ open: false, item: null });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleResyncAttendance = async () => {
    if (!editDialog.item) return;
    try {
      const res = await api.post(`/payroll/${editDialog.item.id}/resync-attendance`);
      setEditForm(f => ({ ...f, present_days: res.data.present_days ?? 0 }));
      setEditDialog(d => ({ ...d, item: res.data }));
      toast.success(`Synced from punch-ins: ${res.data.present_days} days`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPayslip = (item) => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/payroll/${item.id}/payslip-pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `payslip_${item.employee_code}_${item.month}.pdf`;
        a.click();
      });
  };

  const statusColor = (s) => ({
    draft: 'bg-amber-100 text-amber-700',
    finalized: 'bg-blue-100 text-blue-700',
    paid: 'bg-emerald-100 text-emerald-700',
    on_hold: 'bg-red-100 text-red-700 border border-red-200',
  }[s] || 'bg-neutral-100 text-neutral-700');

  // Compute live preview when editing
  const livePreview = useMemo(() => {
    const basic = parseFloat(editForm.basic_salary) || 0;
    const total = editDialog.item?.total_days || 31;
    const paidOffs = parseInt(editDialog.item?.paid_weekly_offs ?? 4) || 0;
    const working = Math.max(total - paidOffs, 1);
    const present = parseFloat(editForm.present_days) || 0;
    const incentive = parseFloat(editForm.incentive) || 0;
    const ta = parseFloat(editDialog.item?.travel_allowance) || 0;
    const advance = parseFloat(editForm.advance) || 0;
    const penalty = parseFloat(editForm.penalty) || 0;
    const other = parseFloat(editForm.other_deductions) || 0;
    const brandSal = parseFloat(editForm.brand_salary) || 0;
    const isBrand = !!editForm.is_brand_staff || brandSal > 0;
    const pfOn = editForm.pf_applicable !== false;
    const esiOn = editForm.esi_applicable !== false;
    const ptOn = !!editForm.pt_applicable;
    const lwfOn = !!editForm.lwf_applicable;
    const lwfAmt = parseFloat(editForm.lwf_amount) || 50;
    const per_day = total ? basic / total : 0;
    const adjustment = (present - working) * per_day;
    const earned = basic + adjustment;
    const gross = earned + incentive + ta;
    const pf_base = Math.min(basic, 15000);
    const pf = pfOn ? pf_base * 0.12 : 0;
    const esi = esiOn ? gross * 0.0075 : 0;
    // Kerala PT slab — keep this in sync with backend `_pt_for_gross`
    const pt = ptOn ? (
      gross < 12000 ? 0 :
      gross < 18000 ? 120 :
      gross < 25000 ? 180 :
      gross < 35000 ? 240 :
      gross < 45000 ? 300 :
      gross < 55000 ? 420 : 600
    ) : 0;
    const lwf = lwfOn ? lwfAmt : 0;
    const total_deduct = pf + esi + pt + lwf + advance + penalty + other;
    const payable = gross - total_deduct;
    const brand25 = brandSal * 0.25;
    const totalWithBrand = payable + brand25;
    const netPayable = totalWithBrand - brandSal;
    const comp = isBrand ? basic * 0.25 : 0;
    return { working, per_day, adjustment, earned, gross, pf, esi, pt, lwf, ta, total_deduct, payable, brand25, totalWithBrand, netPayable, comp, brandSal, paidOffs };
  }, [editForm, editDialog.item]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Payroll Management</h1>
          <p className="text-neutral-500 text-sm mt-1">
            {isAdmin ? 'Earned = Basic ± (Working Days − Present) × Per Day · PF 12% (max ₹1,800) · ESI 0.75% of Gross' : 'View your payslips'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="payroll-month-filter" />
          {isAdmin && (
            <>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger className="rounded-sm w-44" data-testid="payroll-branch-filter">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" className="rounded-sm" onClick={handleExportCsv} data-testid="payroll-export-csv">
                <Download className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button variant="outline" className="rounded-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={handleExportExcel} data-testid="payroll-export-excel">
                <Download className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={handleRunPayroll} data-testid="run-payroll-button">
                <Play className="h-4 w-4 mr-2" /> Run Payroll
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-sm">
          {isAdmin && <TabsTrigger value="runs" className="rounded-sm" data-testid="payroll-tab-runs">Payroll Runs</TabsTrigger>}
          {isAdmin && <TabsTrigger value="structures" className="rounded-sm" data-testid="payroll-tab-structures">Basic Salary</TabsTrigger>}
          {!isAdmin && <TabsTrigger value="mine" className="rounded-sm" data-testid="payroll-tab-mine">My Payslips</TabsTrigger>}
        </TabsList>

        {/* ── Payroll Runs ── */}
        <TabsContent value="runs" className="mt-4 space-y-4">
          {/* Summary stat strip */}
          {payrolls.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payroll-summary">
              {(() => {
                const totals = payrolls.reduce((acc, p) => {
                  acc.gross += p.gross_salary || 0;
                  acc.deduct += p.total_deductions || 0;
                  acc.payable += p.salary_payable || 0;
                  const outflow = p.brand_salary ? (p.net_payable || 0) : (p.salary_payable || 0);
                  acc.outflow += outflow;
                  return acc;
                }, { gross: 0, deduct: 0, payable: 0, outflow: 0 });
                const cards = [
                  { label: 'Gross Payroll', value: totals.gross, color: 'bg-blue-50 text-blue-700 border-blue-100' },
                  { label: 'Total Deductions', value: totals.deduct, color: 'bg-amber-50 text-amber-700 border-amber-100' },
                  { label: 'Salary Payable', value: totals.payable, color: 'bg-neutral-50 text-neutral-700 border-neutral-200' },
                  { label: 'Konattu Outflow', value: totals.outflow, color: 'bg-red-50 text-red-700 border-red-100' },
                ];
                return cards.map((c) => (
                  <div key={c.label} className={`border rounded-md p-3 ${c.color}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{c.label}</p>
                    <p className="text-lg font-bold tracking-tight mt-0.5">{inr(c.value)}</p>
                    <p className="text-[10px] opacity-60 mt-0.5">{payrolls.length} employees · {month}</p>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* Bulk action toolbar — only renders when admin has any drafts */}
          {isAdmin && draftIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-sm flex-wrap" data-testid="payroll-bulk-toolbar">
              <span className="text-xs text-neutral-600">
                <strong>{selectedIds.size}</strong> of {draftIds.length} draft row(s) selected
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={toggleSelectAll} data-testid="payroll-select-all-drafts">
                  {allDraftSelected ? 'Clear All' : 'Select All Drafts'}
                </Button>
                <Button size="sm" className="rounded-sm h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={selectedIds.size === 0}
                  onClick={handleBulkFinalize} data-testid="payroll-bulk-finalize">
                  <Check className="h-3.5 w-3.5 mr-1" /> Bulk Approve ({selectedIds.size})
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm h-8 border-red-200 text-red-700 hover:bg-red-50"
                  disabled={selectedIds.size === 0}
                  onClick={handleBulkDelete} data-testid="payroll-bulk-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Bulk Delete
                </Button>
              </div>
            </div>
          )}

          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50 border-b border-neutral-200">
                    {isAdmin && (
                      <TableHead className="w-8 pl-3">
                        <input type="checkbox" checked={allDraftSelected}
                          onChange={toggleSelectAll}
                          disabled={draftIds.length === 0}
                          data-testid="payroll-header-select-all" />
                      </TableHead>
                    )}
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 py-3">Code</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Monthly</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-center">Days</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right bg-blue-50/40">Gross</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right bg-amber-50/40">Deductions</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Salary Payable</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Brand Sal.</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-red-700 text-right bg-red-50/40">Net Payout</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={isAdmin ? 12 : 11}><div className="h-10 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>)
                  : payrolls.length === 0 ? (
                    <TableRow><TableCell colSpan={isAdmin ? 12 : 11} className="text-center py-12 text-neutral-500">
                      <Wallet className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
                      <p className="text-sm">No payroll for {month}.</p>
                      <p className="text-xs mt-1">Set basic salaries, then click <strong>Run Payroll</strong>.</p>
                    </TableCell></TableRow>
                  ) : payrolls.map(p => {
                    const deductTip = `PF ${inr(p.pf)} · ESI ${inr(p.esi)} · Advance ${inr(p.advance)} · Penalty ${inr(p.penalty)} · Other ${inr(p.other_deductions)}`;
                    const workingDays = p.working_days ?? (p.total_days - (p.paid_weekly_offs ?? 4));
                    const netPayout = p.brand_salary ? p.net_payable : p.salary_payable;
                    const isDraftRow = p.status === 'draft' || p.status === 'on_hold';
                    return (
                      <TableRow key={p.id} className={`hover:bg-neutral-50 border-b border-neutral-100 ${p.status === 'on_hold' ? 'bg-red-50/40' : ''} ${selectedIds.has(p.id) ? 'bg-emerald-50/40' : ''}`} data-testid={`payroll-row-${p.id}`} title={p.status === 'on_hold' ? p.on_hold_reason : ''}>
                        {isAdmin && (
                          <TableCell className="pl-3 py-3 w-8">
                            {isDraftRow ? (
                              <input type="checkbox"
                                checked={selectedIds.has(p.id)}
                                onChange={() => toggleOne(p.id)}
                                data-testid={`payroll-row-select-${p.id}`} />
                            ) : (
                              <span className="block w-3 h-3" />
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-xs font-mono text-neutral-500 py-3">{p.employee_code}</TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-neutral-900">{p.employee_name}</span>
                            {p.is_brand_staff && <Badge className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-100 font-normal">{p.brand_classification || 'Brand'}</Badge>}
                            {p.warnings?.length > 0 && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title={p.warnings.join(', ')} />}
                          </div>
                          <p className="text-[10px] text-neutral-400 mt-0.5">{p.job_role} · {p.branch_name}</p>
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums text-neutral-700">{inr(p.basic_salary)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className={`text-sm font-bold tabular-nums ${p.present_days < workingDays ? 'text-amber-600' : p.present_days > workingDays ? 'text-emerald-600' : 'text-neutral-900'}`}>
                              {p.present_days}
                            </span>
                            <span className="text-[10px] text-neutral-500 tabular-nums">of {p.total_days} days</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums font-semibold text-blue-700 bg-blue-50/30">{inr(p.gross_salary)}</TableCell>
                        <TableCell className="text-sm text-right tabular-nums text-amber-700 bg-amber-50/30" title={deductTip}>
                          -{inr(p.total_deductions)}
                          <p className="text-[9px] text-amber-500 font-normal mt-0.5">PF·ESI·Adv·Pen</p>
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums font-semibold text-neutral-900">{inr(p.salary_payable)}</TableCell>
                        <TableCell className="text-sm text-right tabular-nums text-indigo-700">{p.brand_salary ? inr(p.brand_salary) : <span className="text-neutral-300">—</span>}</TableCell>
                        <TableCell className="text-right bg-red-50/30">
                          <span className="text-base font-bold tabular-nums text-red-700">{inr(netPayout)}</span>
                        </TableCell>
                        <TableCell><Badge className={`text-[10px] capitalize font-semibold ${statusColor(p.status)}`}>{p.status}</Badge></TableCell>
                        <TableCell className="text-right pr-4">
                          <div className="flex gap-0.5 justify-end">
                            {p.status !== 'paid' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`edit-payroll-${p.id}`} title="Edit">
                                <Settings className="h-3.5 w-3.5 text-neutral-600" />
                              </Button>
                            )}
                            {p.status === 'draft' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleFinalize(p.id)} data-testid={`finalize-payroll-${p.id}`} title="Finalize">
                                <Check className="h-3.5 w-3.5 text-blue-600" />
                              </Button>
                            )}
                            {p.status === 'finalized' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleMarkPaid(p.id)} data-testid={`pay-payroll-${p.id}`} title="Mark Paid">
                                <CircleDollarSign className="h-3.5 w-3.5 text-emerald-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadPayslip(p)} data-testid={`payslip-${p.id}`} title="Download Payslip">
                              <Download className="h-3.5 w-3.5 text-neutral-600" />
                            </Button>
                            {isAdmin && p.status !== 'paid' && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRemoveRow(p)} data-testid={`remove-payroll-${p.id}`} title="Remove row">
                                <Trash2 className="h-3.5 w-3.5 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ── Basic Salary (Structures) ── */}
        <TabsContent value="structures" className="mt-4">
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-neutral-700">Basic Salary per Employee</CardTitle>
              <p className="text-xs text-neutral-500">Set each employee's Basic Salary. PF, ESI, Incentive, Advance are handled at payroll time.</p>
            </CardHeader>
            <CardContent className="pt-0 p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-neutral-50">
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Code</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Role</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Type</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Monthly Salary</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Weekly Offs</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Deductions</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.filter(e => ['employee', 'branch_manager', 'management'].includes(e.system_role)).map(emp => {
                      const s = structMap[emp.id];
                      const isBrand = emp.brand_classification && emp.brand_classification !== 'Konattu';
                      return (
                        <TableRow key={emp.id} className="hover:bg-neutral-50" data-testid={`struct-row-${emp.id}`}>
                          <TableCell className="text-xs font-mono">{emp.employee_code}</TableCell>
                          <TableCell className="text-sm font-medium">{emp.name}</TableCell>
                          <TableCell className="text-xs text-neutral-600">{emp.job_role}</TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${isBrand ? 'bg-red-50 text-red-700 border-red-100' : 'bg-neutral-100 text-neutral-700'}`}>
                              {isBrand ? `Brand (${emp.brand_classification})` : 'Konattu'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-right">{s?.basic_salary ? inr(s.basic_salary) : <span className="text-neutral-400">Not set</span>}</TableCell>
                          <TableCell className="text-sm text-right">{s ? (s.paid_weekly_offs ?? 4) : <span className="text-neutral-400">—</span>}</TableCell>
                          <TableCell className="text-xs">
                            <div className="flex gap-1">
                              <Badge className={`text-[9px] ${s?.pf_applicable !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-neutral-100 text-neutral-500'}`}>
                                PF {s?.pf_applicable !== false ? 'On' : 'Off'}
                              </Badge>
                              <Badge className={`text-[9px] ${s?.esi_applicable !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-neutral-100 text-neutral-500'}`}>
                                ESI {s?.esi_applicable !== false ? 'On' : 'Off'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openStructDialog(emp)} data-testid={`set-struct-${emp.id}`}>
                              {s?.basic_salary ? 'Edit' : 'Set'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── My Payslips ── */}
        <TabsContent value="mine" className="mt-4">
          {payrolls.length === 0 ? (
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardContent className="py-16 text-center text-neutral-500">
                <Wallet className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
                <p className="text-sm">No payslips yet for {month}.</p>
                <p className="text-xs mt-1 text-neutral-400">Your HR will generate payroll at month-end.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="my-payslips-grid">
              {payrolls.map(p => {
                const workingDays = p.working_days ?? (p.total_days - (p.paid_weekly_offs ?? 4));
                const netPayout = p.brand_salary ? p.net_payable : p.salary_payable;
                return (
                  <Card key={p.id} className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
                    {/* Header strip */}
                    <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider opacity-60">Payslip</p>
                        <h3 className="font-heading font-bold text-lg">{p.month}</h3>
                      </div>
                      <Badge className={`text-[10px] capitalize font-semibold ${statusColor(p.status)}`}>{p.status}</Badge>
                    </div>
                    {/* Headline: Net Payout */}
                    <div className="bg-red-50 border-b border-red-100 px-4 py-3">
                      <p className="text-[10px] uppercase tracking-wider text-red-700/70 font-bold">Net Take-Home</p>
                      <p className="text-2xl font-bold text-red-700 tabular-nums">{inr(netPayout)}</p>
                    </div>
                    {/* Breakdown */}
                    <div className="divide-y divide-neutral-100 text-sm">
                      <div className="flex justify-between px-4 py-2">
                        <span className="text-neutral-500">Monthly Salary</span>
                        <span className="tabular-nums text-neutral-900">{inr(p.basic_salary)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2">
                        <span className="text-neutral-500">Days Worked</span>
                        <span className="tabular-nums text-neutral-900">{p.present_days}<span className="text-neutral-400"> / {workingDays}</span></span>
                      </div>
                      <div className="flex justify-between px-4 py-2 bg-blue-50/30">
                        <span className="text-neutral-600 font-medium">Gross Salary</span>
                        <span className="tabular-nums font-semibold text-blue-700">{inr(p.gross_salary)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2 bg-amber-50/30" title={`PF ${inr(p.pf)} · ESI ${inr(p.esi)} · Advance ${inr(p.advance)} · Penalty ${inr(p.penalty)} · Other ${inr(p.other_deductions)}`}>
                        <span className="text-neutral-600 font-medium">Total Deductions</span>
                        <span className="tabular-nums text-amber-700">-{inr(p.total_deductions)}</span>
                      </div>
                      <div className="flex justify-between px-4 py-2">
                        <span className="text-neutral-700 font-semibold">Salary Payable</span>
                        <span className="tabular-nums font-semibold text-neutral-900">{inr(p.salary_payable)}</span>
                      </div>
                      {p.brand_salary > 0 && (
                        <>
                          <div className="flex justify-between px-4 py-2 text-xs bg-indigo-50/30">
                            <span className="text-indigo-700">+ 25% of Brand Salary</span>
                            <span className="tabular-nums text-emerald-700">+{inr(p.brand_25_percent)}</span>
                          </div>
                          <div className="flex justify-between px-4 py-2 text-xs bg-indigo-50/30">
                            <span className="text-indigo-700">− Brand Salary (paid by brand)</span>
                            <span className="tabular-nums text-red-600">-{inr(p.brand_salary)}</span>
                          </div>
                        </>
                      )}
                    </div>
                    {/* Footer action */}
                    <div className="border-t border-neutral-100 px-4 py-2 flex justify-end">
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => downloadPayslip(p)} data-testid={`my-payslip-${p.id}`}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Basic Salary Dialog */}
      <Dialog open={structDialog.open} onOpenChange={(o) => setStructDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="struct-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Basic Salary — {structDialog.employee?.name}</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">{structDialog.employee?.employee_code} · {structDialog.employee?.job_role}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Monthly Salary</Label>
                <Input type="number" value={structForm.basic_salary}
                  onChange={e => setStructForm(p => ({ ...p, basic_salary: e.target.value }))}
                  className="rounded-sm" data-testid="struct-basic-input" placeholder="30000" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium">Paid Weekly Offs</Label>
                <Input type="number" min="0" max="10" value={structForm.paid_weekly_offs}
                  onChange={e => setStructForm(p => ({ ...p, paid_weekly_offs: e.target.value }))}
                  className="rounded-sm" data-testid="struct-weekly-offs-input" placeholder="4" />
                <p className="text-[10px] text-neutral-500">Days/month already paid (e.g. 4 Sundays)</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <label className={`flex items-center gap-2 p-2.5 border rounded-sm cursor-pointer ${structForm.pf_applicable ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50'}`}>
                <input type="checkbox" checked={!!structForm.pf_applicable}
                  onChange={e => setStructForm(p => ({ ...p, pf_applicable: e.target.checked }))}
                  data-testid="struct-pf-checkbox" />
                <div className="text-xs">
                  <p className="font-semibold">Deduct PF</p>
                  <p className="text-[10px] text-neutral-500">12% capped at ₹1,800</p>
                </div>
              </label>
              <label className={`flex items-center gap-2 p-2.5 border rounded-sm cursor-pointer ${structForm.esi_applicable ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50'}`}>
                <input type="checkbox" checked={!!structForm.esi_applicable}
                  onChange={e => setStructForm(p => ({ ...p, esi_applicable: e.target.checked }))}
                  data-testid="struct-esi-checkbox" />
                <div className="text-xs">
                  <p className="font-semibold">Deduct ESI</p>
                  <p className="text-[10px] text-neutral-500">0.75% of Gross</p>
                </div>
              </label>
              <label className={`flex items-center gap-2 p-2.5 border rounded-sm cursor-pointer ${structForm.pt_applicable ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50'}`}>
                <input type="checkbox" checked={!!structForm.pt_applicable}
                  onChange={e => setStructForm(p => ({ ...p, pt_applicable: e.target.checked }))}
                  data-testid="struct-pt-checkbox" />
                <div className="text-xs">
                  <p className="font-semibold">Deduct PT</p>
                  <p className="text-[10px] text-neutral-500">Kerala slab on Gross</p>
                </div>
              </label>
              <label className={`flex items-center gap-2 p-2.5 border rounded-sm cursor-pointer ${structForm.lwf_applicable ? 'border-neutral-200 bg-white' : 'border-neutral-200 bg-neutral-50'}`}>
                <input type="checkbox" checked={!!structForm.lwf_applicable}
                  onChange={e => setStructForm(p => ({ ...p, lwf_applicable: e.target.checked }))}
                  data-testid="struct-lwf-checkbox" />
                <div className="text-xs">
                  <p className="font-semibold">Deduct LWF</p>
                  <p className="text-[10px] text-neutral-500">₹{structForm.lwf_amount || 50}/month</p>
                </div>
              </label>
            </div>
            {structForm.lwf_applicable && (
              <div className="space-y-1">
                <Label className="text-xs font-medium">LWF Amount (₹/month)</Label>
                <Input type="number" min="0" step="1" value={structForm.lwf_amount}
                  onChange={e => setStructForm(p => ({ ...p, lwf_amount: e.target.value }))}
                  className="rounded-sm w-32" data-testid="struct-lwf-amount-input" placeholder="50" />
              </div>
            )}
            <p className="text-[11px] text-neutral-500">Toggles flow into every payroll run for this employee. PT uses Kerala slabs (₹0–600/month based on Gross). LWF defaults to ₹50/month and is editable.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStructDialog({ open: false, employee: null })} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveStruct} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="struct-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(o) => setEditDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="payroll-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Edit Payroll — {editDialog.item?.employee_name}</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">Month: {editDialog.item?.month}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-5 gap-4 py-2">
            <div className="col-span-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Monthly Salary</Label>
                  <Input type="number" value={editForm.basic_salary} onChange={e => setEditForm(p => ({ ...p, basic_salary: e.target.value }))} className="rounded-sm" data-testid="edit-basic-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Days Present (auto from punch-ins)</Label>
                  <div className="flex gap-1.5">
                    <Input type="number" step="0.5" value={editForm.present_days} readOnly tabIndex={-1}
                      className="rounded-sm bg-neutral-50 font-semibold text-neutral-900 cursor-not-allowed" data-testid="edit-present-input" />
                    <Button type="button" variant="outline" size="icon" onClick={handleResyncAttendance}
                      className="rounded-sm h-10 w-10 flex-shrink-0" title="Re-sync from attendance punch-ins"
                      data-testid="resync-attendance-btn">
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-neutral-500">of {editDialog.item?.total_days} total · {editDialog.item?.working_days ?? ((editDialog.item?.total_days ?? 30) - (editDialog.item?.paid_weekly_offs ?? 4))} working</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Incentive (manual)</Label>
                  <Input type="number" value={editForm.incentive} onChange={e => setEditForm(p => ({ ...p, incentive: e.target.value }))} className="rounded-sm" data-testid="edit-incentive-input" placeholder="0" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Advance (manual)</Label>
                  <Input type="number" value={editForm.advance} onChange={e => setEditForm(p => ({ ...p, advance: e.target.value }))} className="rounded-sm" data-testid="edit-advance-input" placeholder="0" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Penalty (manual)</Label>
                  <Input type="number" value={editForm.penalty} onChange={e => setEditForm(p => ({ ...p, penalty: e.target.value }))} className="rounded-sm" data-testid="edit-penalty-input" placeholder="0" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium">Other Deductions</Label>
                  <Input type="number" value={editForm.other_deductions} onChange={e => setEditForm(p => ({ ...p, other_deductions: e.target.value }))} className="rounded-sm" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium">Brand Salary <span className="text-[10px] text-neutral-500 font-normal">(paid by brand directly to employee — Konattu keeps 25%)</span></Label>
                  <Input type="number" value={editForm.brand_salary}
                    onChange={e => setEditForm(p => ({ ...p, brand_salary: e.target.value }))}
                    className="rounded-sm" data-testid="edit-brand-salary-input" placeholder="0" />
                </div>
                <div className="space-y-1 col-span-2">
                  <label className="flex items-center gap-2 p-2.5 border border-neutral-200 rounded-sm cursor-pointer hover:bg-neutral-50">
                    <input type="checkbox" checked={!!editForm.is_brand_staff}
                      onChange={e => setEditForm(p => ({ ...p, is_brand_staff: e.target.checked }))}
                      data-testid="edit-brand-checkbox" />
                    <span className="text-sm">Brand / Company Staff <span className="text-xs text-neutral-500">(shows 25% company cost, not added to payable)</span></span>
                  </label>
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 p-2.5 border border-neutral-200 rounded-sm cursor-pointer hover:bg-neutral-50">
                    <input type="checkbox" checked={editForm.pf_applicable !== false}
                      onChange={e => setEditForm(p => ({ ...p, pf_applicable: e.target.checked }))}
                      data-testid="edit-pf-checkbox" />
                    <span className="text-sm">Deduct PF <span className="text-xs text-neutral-500">(this month)</span></span>
                  </label>
                  <label className="flex items-center gap-2 p-2.5 border border-neutral-200 rounded-sm cursor-pointer hover:bg-neutral-50">
                    <input type="checkbox" checked={editForm.esi_applicable !== false}
                      onChange={e => setEditForm(p => ({ ...p, esi_applicable: e.target.checked }))}
                      data-testid="edit-esi-checkbox" />
                    <span className="text-sm">Deduct ESI <span className="text-xs text-neutral-500">(this month)</span></span>
                  </label>
                  <label className="flex items-center gap-2 p-2.5 border border-neutral-200 rounded-sm cursor-pointer hover:bg-neutral-50">
                    <input type="checkbox" checked={!!editForm.pt_applicable}
                      onChange={e => setEditForm(p => ({ ...p, pt_applicable: e.target.checked }))}
                      data-testid="edit-pt-checkbox" />
                    <span className="text-sm">Deduct PT <span className="text-xs text-neutral-500">(Kerala slab)</span></span>
                  </label>
                  <label className="flex items-center gap-2 p-2.5 border border-neutral-200 rounded-sm cursor-pointer hover:bg-neutral-50">
                    <input type="checkbox" checked={!!editForm.lwf_applicable}
                      onChange={e => setEditForm(p => ({ ...p, lwf_applicable: e.target.checked }))}
                      data-testid="edit-lwf-checkbox" />
                    <span className="text-sm">Deduct LWF <span className="text-xs text-neutral-500">(₹{editForm.lwf_amount || 50})</span></span>
                  </label>
                </div>
                {editForm.lwf_applicable && (
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs font-medium">LWF Amount (₹)</Label>
                    <Input type="number" min="0" step="1" value={editForm.lwf_amount}
                      onChange={e => setEditForm(p => ({ ...p, lwf_amount: e.target.value }))}
                      className="rounded-sm w-32" data-testid="edit-lwf-amount-input" />
                  </div>
                )}
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium">Notes</Label>
                  <Textarea value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="rounded-sm" />
                </div>
              </div>
            </div>
            {/* Live Preview */}
            <div className="col-span-2 bg-neutral-50 rounded-sm p-3 space-y-1.5 text-xs">
              <p className="font-bold uppercase tracking-wider text-neutral-500 mb-1">Live Preview</p>
              <div className="flex justify-between"><span>Total / Paid Weekly Offs</span><span>{editDialog.item?.total_days ?? 31} / {livePreview.paidOffs}</span></div>
              <div className="flex justify-between"><span>Working Days</span><span className="font-semibold">{livePreview.working}</span></div>
              <div className="flex justify-between"><span>Per Day Salary</span><span>{inr(livePreview.per_day)}</span></div>
              {livePreview.adjustment < 0 && (
                <div className="flex justify-between text-red-600"><span>− Deduction ({(livePreview.working - (parseFloat(editForm.present_days)||0)).toFixed(1)} days)</span><span>-{inr(Math.abs(livePreview.adjustment))}</span></div>
              )}
              {livePreview.adjustment > 0 && (
                <div className="flex justify-between text-emerald-700"><span>+ Addition ({((parseFloat(editForm.present_days)||0) - livePreview.working).toFixed(1)} extra days)</span><span>+{inr(livePreview.adjustment)}</span></div>
              )}
              <div className="flex justify-between"><span>Earned Salary</span><span>{inr(livePreview.earned)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>+ Incentive</span><span>{inr(parseFloat(editForm.incentive)||0)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>+ Travel Allowance (approved)</span><span>{inr(livePreview.ta)}</span></div>
              <div className="flex justify-between font-bold text-neutral-900 bg-blue-50 px-2 py-1 rounded-sm"><span>Net Salar (Gross)</span><span>{inr(livePreview.gross)}</span></div>
              <div className="flex justify-between text-red-600"><span>− PF</span><span>-{inr(livePreview.pf)}</span></div>
              <div className="flex justify-between text-red-600"><span>− ESI</span><span>-{inr(livePreview.esi)}</span></div>
              {editForm.pt_applicable && (
                <div className="flex justify-between text-red-600"><span>− Professional Tax</span><span>-{inr(livePreview.pt)}</span></div>
              )}
              {editForm.lwf_applicable && (
                <div className="flex justify-between text-red-600"><span>− LWF</span><span>-{inr(livePreview.lwf)}</span></div>
              )}
              <div className="flex justify-between text-red-600"><span>− Advance</span><span>-{inr(parseFloat(editForm.advance)||0)}</span></div>
              <div className="flex justify-between text-red-600"><span>− Penalty</span><span>-{inr(parseFloat(editForm.penalty)||0)}</span></div>
              <div className="flex justify-between text-red-600"><span>− Other</span><span>-{inr(parseFloat(editForm.other_deductions)||0)}</span></div>
              <div className={`flex justify-between font-bold px-2 py-1.5 rounded-sm mt-2 ${livePreview.brandSal > 0 ? 'bg-blue-50 text-neutral-900' : 'bg-red-600 text-white'}`}><span>SALARY PAYABLE</span><span>{inr(livePreview.payable)}</span></div>
              {livePreview.brandSal > 0 && (
                <>
                  <div className="flex justify-between text-emerald-700"><span>+ 25% of Brand Salary</span><span>+{inr(livePreview.brand25)}</span></div>
                  <div className="flex justify-between text-red-600"><span>− Brand Salary</span><span>-{inr(livePreview.brandSal)}</span></div>
                  <div className="flex justify-between font-bold text-white bg-red-600 px-2 py-1.5 rounded-sm" data-testid="live-preview-net-payable"><span>NET PAYABLE</span><span>{inr(livePreview.netPayable)}</span></div>
                </>
              )}
              {editForm.is_brand_staff && !(livePreview.brandSal > 0) && (
                <div className="mt-2 pt-2 border-t border-neutral-200">
                  <div className="flex justify-between text-blue-700 text-[10px]"><span>+25% Company Cost (info)</span><span>{inr(livePreview.comp)}</span></div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, item: null })} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="payroll-save-button">Save & Recompute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
